import React from "react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { BlurhashImage } from "./blurhash-image";
import { cn } from "@/lib/utils";
import { ImageIcon } from "lucide-react";

interface PropertyImageThumbnailProps {
  images: Array<{
    key: string;
    filename: string;
    contentType?: string;
    size?: number;
    blurhash?: string;
  }>;
  className?: string;
  size?: "sm" | "md" | "lg";
  showCount?: boolean;
  maxImages?: number;
}

const sizeClasses = {
  sm: "w-8 h-8",
  md: "w-12 h-12",
  lg: "w-16 h-16",
};

const sizeDimensions = {
  sm: { width: 32, height: 32 },
  md: { width: 48, height: 48 },
  lg: { width: 64, height: 64 },
};

const getFlexibleDimensions = (
  className?: string,
  size?: "sm" | "md" | "lg",
) => {
  if (className?.includes("w-full")) {
    return { width: undefined, height: 120 }; // let width be flexible, set reasonable height
  }
  return size ? sizeDimensions[size] : sizeDimensions.md;
};

export function PropertyImageThumbnail({
  images,
  className,
  size = "md",
  showCount = true,
  maxImages = 3,
}: PropertyImageThumbnailProps) {
  if (!images || images.length === 0) {
    return (
      <div
        className={cn(
          "bg-muted rounded border-2 border-dashed border-muted-foreground/25 flex items-center justify-center",
          sizeClasses[size],
          className,
        )}
      >
        <ImageIcon className="w-4 h-4 text-muted-foreground/50" />
      </div>
    );
  }

  const dimensions = getFlexibleDimensions(className, size);
  const isFullWidth = className?.includes("w-full");
  const previewCount =
    isFullWidth && showCount ? Math.max(1, maxImages - 1) : maxImages;
  const displayImages = images.slice(0, previewCount);
  const remainingCount = Math.max(0, images.length - previewCount);

  const imageUrls = useQuery(
    api.r2.getImageUrls,
    displayImages.length > 0
      ? {
          keys: displayImages.map((img) => img.key),
          expiresIn: 60 * 60 * 24, // 24 hours
        }
      : "skip",
  );

  // loading state
  if (imageUrls === undefined) {
    return (
      <div
        className={cn(
          "bg-muted rounded border animate-pulse flex items-center justify-center",
          isFullWidth ? "w-full h-[120px]" : sizeClasses[size],
          className,
        )}
      >
        <ImageIcon className="w-4 h-4 text-muted-foreground/50" />
      </div>
    );
  }

  if (displayImages.length === 1) {
    const image = displayImages[0];
    const imageUrl = imageUrls?.[0];

    // if no signed URL available, show placeholder
    if (!imageUrl) {
      return (
        <div
          className={cn(
            "bg-muted rounded border-2 border-dashed border-muted-foreground/25 flex items-center justify-center",
            isFullWidth ? "w-full h-[120px]" : sizeClasses[size],
            className,
          )}
        >
          <ImageIcon className="w-4 h-4 text-muted-foreground/50" />
        </div>
      );
    }

    return (
      <div className={cn("relative", className)}>
        <BlurhashImage
          src={imageUrl}
          blurhash={image.blurhash}
          alt={image.filename}
          width={dimensions.width}
          height={dimensions.height}
          className={cn(
            "rounded border",
            isFullWidth ? "w-full h-[120px]" : sizeClasses[size],
          )}
          objectFit="cover"
        />
        {showCount && images.length > 1 && (
          <div className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
            {images.length}
          </div>
        )}
      </div>
    );
  }

  if (isFullWidth) {
    return (
      <div
        className={cn(
          "grid w-full grid-cols-2 gap-2 overflow-hidden sm:grid-cols-4 cursor-pointer",
          className,
        )}
      >
        {displayImages.map((image, index) => {
          const imageUrl = imageUrls?.[index];

          if (!imageUrl) {
            return (
              <div
                key={image.key}
                className="h-[120px] min-w-0 rounded border-2 border-dashed border-muted-foreground/25 bg-muted"
              >
                <div className="flex h-full items-center justify-center">
                  <ImageIcon className="h-4 w-4 text-muted-foreground/50" />
                </div>
              </div>
            );
          }

          return (
            <div
              key={image.key}
              className="relative flex h-[140px] min-w-0 items-center justify-center overflow-hidden rounded border bg-muted/30"
            >
              <BlurhashImage
                src={imageUrl}
                blurhash={image.blurhash}
                alt={image.filename}
                width={undefined}
                height={140}
                className="h-full w-full"
                objectFit="contain"
              />
            </div>
          );
        })}

        {remainingCount > 0 && showCount && (
          <div className="flex h-[140px] min-w-0 items-center justify-center rounded border bg-muted text-xs font-medium text-muted-foreground">
            +{remainingCount}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex -space-x-2", className)}>
      {displayImages.map((image, index) => {
        const imageUrl = imageUrls?.[index];

        // if no signed URL available, show placeholder
        if (!imageUrl) {
          return (
            <div
              key={image.key}
              className={cn(
                "bg-muted rounded border-2 border-dashed border-muted-foreground/25 flex items-center justify-center",
                isFullWidth ? "w-full h-[120px]" : sizeClasses[size],
              )}
              style={{ zIndex: displayImages.length - index }}
            >
              <ImageIcon className="w-4 h-4 text-muted-foreground/50" />
            </div>
          );
        }

        return (
          <div
            key={image.key}
            className="relative border-2 border-background rounded"
            style={{ zIndex: displayImages.length - index }}
          >
            <BlurhashImage
              src={imageUrl}
              blurhash={image.blurhash}
              alt={image.filename}
              width={dimensions.width}
              height={dimensions.height}
              className={cn(
                "rounded",
                isFullWidth ? "w-full h-[120px]" : sizeClasses[size],
              )}
              objectFit="cover"
            />
          </div>
        );
      })}

      {remainingCount > 0 && showCount && (
        <div
          className={cn(
            "bg-muted border-2 border-background rounded flex items-center justify-center text-xs font-medium text-muted-foreground",
            isFullWidth ? "w-full h-[120px]" : sizeClasses[size],
          )}
          style={{ zIndex: 0 }}
        >
          +{remainingCount}
        </div>
      )}
    </div>
  );
}
